<script setup>
import { formatDistanceToNow, format } from 'date-fns'
import { computed } from 'vue'
import { track } from '@/lib/usageTracker'
import { Loader2 } from 'lucide-vue-next'
import { Button } from '@/components/ui'
import TaskStatusBadge from './TaskStatusBadge.vue'

const props = defineProps({
  tasks: { type: Array, default: () => [] },
  onRetry: { type: Function, default: null },
  retryingTaskId: { type: Object, default: null },
})

const deadLetteredTasks = computed(() => props.tasks.filter(t => t.status === 'dead_lettered'))
const normalTasks = computed(() => props.tasks.filter(t => t.status !== 'dead_lettered'))

const MAX_VISIBLE = 5
const displayedDeadLettered = computed(() => deadLetteredTasks.value.slice(0, MAX_VISIBLE))
const displayedNormal = computed(() => normalTasks.value.slice(0, MAX_VISIBLE))

const viewMoreTo = computed(() => {
  if (deadLetteredTasks.value.length > 0) return '/app/tasks?status=dead_lettered'
  return '/app/tasks'
})

const formatAbsolute = (dateStr) => {
  if (!dateStr) return ''
  return format(new Date(dateStr), "MMM d, yyyy 'at' h:mm a")
}
</script>

<template>
  <div class="activity-feed">
    <!-- Dead-lettered section (prominent highlight, top of feed) -->
    <div v-if="displayedDeadLettered.length > 0" class="feed-section dead-letter-section">
      <h4 class="section-label">Dead-Lettered</h4>
      <div v-for="task in displayedDeadLettered"
           :key="task.taskId"
           class="feed-item dead-letter-item"
           :title="formatAbsolute(task.updatedAt)">
        <div class="feed-item-row">
          <TaskStatusBadge :status="task.status" />
          <span class="entity-type">{{ task.entityType }}</span>
          <span class="task-id">{{ task.taskId }}</span>
          <span class="timestamp">{{ formatDistanceToNow(new Date(task.updatedAt)) }}</span>
        </div>
        <div class="feed-item-row feed-item-meta">
          <span class="external-id">{{ task.externalId || '—' }}</span>
          <Button
            v-if="(task.status === 'failed' || task.status === 'dead_lettered') && props.onRetry"
            size="sm"
            variant="outline"
            :loading="props.retryingTaskId === task.taskId"
            @click="() => { track('btn:RetryTask', 'click', { taskId: task.taskId }); props.onRetry(task.taskId) }"
            class="retry-button"
          >
            <Loader2 v-if="props.retryingTaskId === task.taskId" class="h-3 w-3 animate-spin" />
            <span v-else>Retry</span>
          </Button>
        </div>
      </div>
    </div>

    <!-- Normal activity feed -->
    <div class="feed-section">
      <h4 v-if="displayedNormal.length > 0" class="section-label">Recent Activity</h4>
      <div v-for="task in displayedNormal"
           :key="task.taskId"
           class="feed-item"
           :title="formatAbsolute(task.updatedAt)">
        <div class="feed-item-row">
          <TaskStatusBadge :status="task.status" />
          <span class="entity-type">{{ task.entityType }}</span>
          <span class="task-id">{{ task.taskId }}</span>
          <span class="timestamp">{{ formatDistanceToNow(new Date(task.updatedAt)) }}</span>
        </div>
        <div class="feed-item-row feed-item-meta">
          <span class="external-id">{{ task.externalId || '—' }}</span>
          <Button
            v-if="(task.status === 'failed' || task.status === 'dead_lettered') && props.onRetry"
            size="sm"
            variant="outline"
            :loading="props.retryingTaskId === task.taskId"
            @click="() => { track('btn:RetryTask', 'click', { taskId: task.taskId }); props.onRetry(task.taskId) }"
            class="retry-button"
          >
            <Loader2 v-if="props.retryingTaskId === task.taskId" class="h-3 w-3 animate-spin" />
            <span v-else>Retry</span>
          </Button>
        </div>
      </div>
      <div v-if="tasks.length > MAX_VISIBLE" class="feed-footer">
        <router-link :to="viewMoreTo" class="view-more-link">
          View {{ tasks.length - MAX_VISIBLE }} more
        </router-link>
      </div>
    </div>

    <div v-if="tasks.length === 0" class="empty-state">
      No recent task activity.
    </div>
  </div>
</template>

<style scoped>
.activity-feed { display: flex; flex-direction: column; gap: 0.75rem; }
.feed-section { display: flex; flex-direction: column; gap: 0.5rem; }
.section-label {
  font-size: 0.75rem; font-weight: 600; text-transform: uppercase;
  color: var(--color-muted-foreground); margin-bottom: 0.25rem;
}
.feed-item {
  display: flex; flex-direction: column; gap: 0.25rem;
  padding: 0.5rem; border-radius: var(--radius-md); background: var(--color-card);
}
.feed-item-row { display: flex; align-items: center; gap: 0.5rem; }
.feed-item-meta { justify-content: space-between; }
.dead-letter-item {
  border-left: 3px solid var(--color-destructive);
  background: color-mix(in srgb, var(--color-destructive) 8%, transparent);
}
.entity-type { font-weight: 500; font-size: 0.875rem; }
.task-id { font-family: monospace; font-size: 0.75rem; color: var(--color-muted-foreground); }
.external-id { font-size: 0.875rem; color: var(--color-muted-foreground); }
.timestamp {
  margin-left: auto; font-size: 0.75rem; color: var(--color-muted-foreground);
}
.empty-state { color: var(--color-muted-foreground); font-size: 0.875rem; padding: 1rem; }
.feed-footer { display: flex; justify-content: center; padding-top: 0.25rem; }
.view-more-link {
  color: var(--color-primary); font-size: 0.78rem; text-decoration: none;
}
.view-more-link:hover { text-decoration: underline; }
</style>
